// =============================================================================
// ELEVENLABS CALL LOG: the voice-path cost + failure meter
//
// Mirrors `ai_call_log` (which captures Anthropic usage), for the OTHER vendor.
// Today nothing records ElevenLabs calls, latencies, or failures, so "it is
// throwing 500s" is a mystery instead of a query. The Living Corpus v2 (Studio)
// transport leans hard on ElevenLabs (TTS, STT, the Agents WebRTC session, the
// conversation-token mint), so every call gets a row here: which op, the voice,
// status, and round-trip ms. Drift and 4xx storms become observable.
//
// Append-only. No PII beyond the userId already on every other table. See
// docs/living-corpus-v2-proposal.md section 2.6 (observability).
// =============================================================================

import { index, integer, numeric, pgTable, text, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** One ElevenLabs upstream call. `op` is the logical operation, `caller` the
 *  code site, `status` the HTTP-ish outcome, `ms` the round-trip. `voiceId` is
 *  recorded when the op carried one (tts / token mint) so voice-specific
 *  failures (the account-drift 422/404) surface per voice. */
export const elevenlabsCallLog = pgTable(
  'elevenlabs_call_log',
  {
    id: id(),
    userId: text('user_id').notNull(),
    // The code site that made the call, e.g. 'dna.tts', 'agent_session.token',
    // 'conversation_session.do'. Free-form, matches the ClaudeService `caller`.
    caller: varchar('caller', { length: 64 }).notNull(),
    // The logical ElevenLabs operation: 'tts' | 'stt' | 'list_voices' |
    // 'get_voice' | 'conversation_token' | 'agent_session'. Kept as a string
    // (not an enum) so new ops do not need a migration.
    op: varchar('op', { length: 32 }).notNull(),
    // Outcome: the HTTP status as text ('200', '401', '429') or a sentinel
    // ('ok', 'timeout', 'error') when there was no HTTP response.
    status: varchar('status', { length: 16 }).notNull(),
    // Round-trip in milliseconds. Null only if the call never started.
    ms: integer('ms'),
    // The voice involved, when the op carried one. Null for ops without a voice.
    voiceId: varchar('voice_id', { length: 64 }),
    // ── Cost columns (per-user-spend-caps-2026-07-07.md §3.2a) ──
    // Both NULLABLE: null until an amount is actually known (NO INVENTION — EL
    // did not report it, or this op never carried a cost), never a fabricated
    // number. minutesBilled is conversation-minutes attributed to this call
    // (agent_session.token / conversation_token ops); an in-flight session's
    // live minute meter is tracked separately off wall-clock elapsed, not this
    // column. costMicroUsd is priced from the rate card at log time, mirroring
    // how ai_call_log.costUsd (cost_micro_usd) prices a Claude call.
    minutesBilled: numeric('minutes_billed'),
    costMicroUsd: integer('cost_micro_usd'),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('elevenlabs_call_log_user_idx').on(t.userId),
    opIdx: index('elevenlabs_call_log_op_idx').on(t.op),
    statusIdx: index('elevenlabs_call_log_status_idx').on(t.status),
  }),
);
